/**
 * The door.
 *
 * Two ways through it. Someone arriving from sso.slr.family carries a login
 * hint and never sees this screen at all — the silent redirect fires on mount
 * and completes without a prompt. Everyone else gets one button.
 *
 * The third case is the one worth designing for: a signed-in Google account
 * that is not on the family domain. That person must be told, in plain words,
 * rather than dropped into an application with nothing in it. So they stay
 * signed in just long enough to read the sentence.
 */

import { useEffect, useRef, useState } from 'react';

import {
  completeRedirectSignIn,
  signInWithGoogle,
  signOutLocal,
  trySilentSignIn,
} from '../lib/firebase';
import { clearSsoHint, shouldTrySilentSignIn } from '../lib/sso';
import type { SessionStatus } from '../state/SessionProvider';

export default function SignIn({ status }: { status: SessionStatus }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const attempted = useRef(false);

  // Surface the outcome of a redirect. Without this, a credential handoff
  // blocked by storage partitioning is indistinguishable from never trying.
  useEffect(() => {
    void completeRedirectSignIn();
  }, []);

  useEffect(() => {
    if (status !== 'signed-out' || attempted.current) return;
    if (!shouldTrySilentSignIn()) return;

    attempted.current = true;
    setBusy(true);
    void trySilentSignIn().catch(() => {
      // A stale hint must leave the button, not a loop.
      clearSsoHint();
      setBusy(false);
    });
  }, [status]);

  const wrongDomain = status === 'wrong-domain';

  return (
    <div className="signin">
      <div className="signin__card">
        <div className="signin__mark" aria-hidden="true">
          S
        </div>

        <h1 className="display t-3xl signin__title">SamuelSchool</h1>
        <p className="muted signin__blurb">
          Coursework, announcements and schedules for Samuel&rsquo;s semesters at Rose State College.
        </p>

        {wrongDomain ? (
          <div className="signin__notice" role="alert">
            <p className="strong">That account can&rsquo;t be used here.</p>
            <p className="muted t-sm">
              SamuelSchool is limited to <strong>@slr.family</strong> accounts. Sign out and try the
              one you use for the family portal.
            </p>
            <button
              type="button"
              className="btn btn--signout btn--block"
              onClick={() => void signOutLocal()}
            >
              Sign out
            </button>
          </div>
        ) : (
          <>
            <button
              type="button"
              className="btn btn--primary btn--block signin__go"
              disabled={busy}
              onClick={async () => {
                setError(null);
                setBusy(true);
                try {
                  await signInWithGoogle();
                } catch (err) {
                  const code = (err as { code?: string }).code;
                  // Closing the picker is a decision, not a failure.
                  if (code !== 'auth/popup-closed-by-user' && code !== 'auth/cancelled-popup-request') {
                    setError((err as Error).message ?? 'Sign-in failed. Try again.');
                  }
                  setBusy(false);
                }
              }}
            >
              {busy ? <span className="spinner" /> : null}
              {busy ? 'Signing in…' : 'Continue with Google'}
            </button>

            {error ? (
              <p className="field-error" role="alert">
                {error}
              </p>
            ) : null}

            <p className="faint t-sm signin__foot">
              Uses your family Google account. Access to a semester is granted separately.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
